import React, { useState, useEffect, useRef } from 'react';
import { fetchAlternatives, saveSessionSwap, saveSwapReason, clearSessionSwap, searchExercises, SWAP_REASONS } from '../services/swapService';
import ExerciseCard from './ui/ExerciseCard';
import { colors } from '../theme';

const exerciseSubtitle = (ex) => {
  const type = ex.is_bodyweight ? 'Bodyweight' : ex.is_assisted ? 'Assisted' : 'Weighted';
  return ex.movement_category ? `${type} · ${ex.movement_category}` : type;
};

const ExerciseSwapModal = ({ programExercise, userId, currentSwap, onSwapApplied, onClose }) => {
  const [alternatives, setAlternatives] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [swappedTo, setSwappedTo] = useState(null); // set once a swap is saved: shows the "why?" step
  const [error, setError] = useState(null);
  const searchTimeout = useRef(null);

  const movementCategory = programExercise.exercise?.movement_category;
  const originalExerciseId = programExercise.exercise_id || programExercise.exercise?.id;
  const originalName = programExercise.exercise?.name || programExercise.name || 'original exercise';

  useEffect(() => {
    if (!movementCategory || !originalExerciseId) { setLoading(false); return; }
    fetchAlternatives(movementCategory, originalExerciseId).then(data => {
      setAlternatives(data);
      setLoading(false);
    });
  }, [movementCategory, originalExerciseId]);

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    clearTimeout(searchTimeout.current);
    if (!q.trim()) { setSearchResults([]); return; }
    setSearching(true);
    searchTimeout.current = setTimeout(async () => {
      const results = await searchExercises(q.trim(), originalExerciseId);
      setSearchResults(results);
      setSearching(false);
    }, 300);
  };

  // The swap is saved and applied straight away. The reason is asked afterwards, so a client who
  // does not want to give one loses nothing: closing the sheet or tapping Skip leaves the swap in place.
  const handleSelect = async (exercise) => {
    setSaving(true);
    setError(null);
    try {
      await saveSessionSwap(userId, programExercise.id, exercise.id, originalExerciseId);
      onSwapApplied(exercise);
      setSwappedTo(exercise);
    } catch (e) {
      console.error('Swap failed:', e);
      setError('Could not save the swap. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  // The reason is extra information for the coach. If saving it fails the swap still stands, so this
  // closes either way rather than holding the client up mid-workout.
  const handleReason = async (reason) => {
    setSaving(true);
    try {
      await saveSwapReason(userId, programExercise.id, reason);
    } catch (e) {
      console.error('Could not save the swap reason:', e);
    }
    onClose();
  };

  const handleReset = async () => {
    setSaving(true);
    setError(null);
    try {
      await clearSessionSwap(userId, programExercise.id);
      onSwapApplied(null);
      onClose();
    } catch (e) {
      console.error('Reset failed:', e);
      setError('Could not undo the swap. Check your connection and try again.');
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520,
          backgroundColor: colors.surface1,
          borderRadius: '16px 16px 0 0',
          border: '1px solid rgba(42,53,98,0.8)',
          maxHeight: '75vh',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          padding: '16px 16px 12px', borderBottom: '1px solid rgba(42,53,98,0.6)',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>Swap Exercise</div>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
              Today only — your program stays unchanged
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: colors.textMuted, fontSize: 18, cursor: 'pointer', padding: 4 }}
          >
            ✕
          </button>
        </div>

        {swappedTo ? (
          /* Why? Asked only once the swap is saved, and skippable. */
          <div style={{ padding: '16px 16px 24px' }}>
            <div style={{ fontSize: 13, color: colors.success, fontWeight: 600 }}>✓ Swapped to {swappedTo.name}</div>
            <div style={{ fontSize: 15, color: colors.text, fontWeight: 700, marginTop: 16 }}>What made you swap?</div>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>Optional. It helps your coach plan around it.</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
              {SWAP_REASONS.map(reason => (
                <button
                  key={reason.code}
                  onClick={() => handleReason(reason.code)}
                  disabled={saving}
                  style={{
                    padding: '12px 8px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: colors.text, cursor: 'pointer',
                  }}
                >
                  {reason.label}
                </button>
              ))}
            </div>
            <button
              onClick={onClose}
              disabled={saving}
              style={{
                width: '100%', marginTop: 10, padding: '11px 8px', borderRadius: 8,
                background: 'none', border: 'none', fontSize: 13, color: colors.textMuted, cursor: 'pointer',
              }}
            >
              Skip
            </button>
          </div>
        ) : (
        <>
        {error && (
          <div style={{ margin: '10px 16px 0', fontSize: 12, color: colors.danger, flexShrink: 0 }}>{error}</div>
        )}

        {/* Active swap banner */}
        {currentSwap && (
          <div style={{
            margin: '10px 16px 0',
            padding: '10px 12px',
            backgroundColor: 'rgba(52,199,89,0.08)',
            border: '1px solid rgba(52,199,89,0.3)',
            borderRadius: 8,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexShrink: 0,
          }}>
            <span style={{ fontSize: 12, color: colors.success, fontWeight: 600 }}>
              Swapped to: {currentSwap.name}
            </span>
            <button
              onClick={handleReset}
              disabled={saving}
              style={{
                background: 'none', border: '1px solid rgba(255,59,48,0.4)',
                borderRadius: 8, padding: '4px 10px', fontSize: 11,
                color: colors.danger, cursor: 'pointer', fontWeight: 600,
              }}
            >
              Reset to {originalName}
            </button>
          </div>
        )}

        {/* Search */}
        <div style={{ padding: '10px 16px 0', flexShrink: 0 }}>
          <input
            type="text"
            placeholder="Search all exercises…"
            value={searchQuery}
            onChange={handleSearchChange}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '9px 12px', borderRadius: 8, fontSize: 14,
              backgroundColor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: colors.text, outline: 'none',
            }}
          />
        </div>

        {/* List */}
        <div style={{ overflowY: 'auto', padding: '10px 16px 24px', flex: 1 }}>
          {searching ? (
            <div style={{ padding: 24, textAlign: 'center', color: colors.textMuted, fontSize: 14 }}>
              Searching…
            </div>
          ) : searchQuery.trim() ? (
            searchResults.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: colors.textMuted, fontSize: 14 }}>
                No exercises found for "{searchQuery}".
              </div>
            ) : searchResults.map(ex => {
              const isActive = currentSwap?.id === ex.id;
              return (
                <ExerciseCard
                  key={ex.id}
                  image={ex.gif_url}
                  name={ex.name}
                  subtitle={exerciseSubtitle(ex)}
                  selected={isActive}
                  disabled={saving}
                  onClick={() => handleSelect(ex)}
                  trailing={isActive && <span style={{ color: colors.success, fontSize: 16 }}>✓</span>}
                />
              );
            })
          ) : loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: colors.textMuted, fontSize: 14 }}>
              Loading alternatives…
            </div>
          ) : alternatives.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: colors.textMuted, fontSize: 14 }}>
              No alternatives found for this muscle group.
            </div>
          ) : (
            alternatives.map(ex => {
              const isActive = currentSwap?.id === ex.id;
              return (
                <ExerciseCard
                  key={ex.id}
                  image={ex.gif_url}
                  name={ex.name}
                  subtitle={exerciseSubtitle(ex)}
                  selected={isActive}
                  disabled={saving}
                  onClick={() => handleSelect(ex)}
                  trailing={isActive && <span style={{ color: colors.success, fontSize: 16 }}>✓</span>}
                />
              );
            })
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default ExerciseSwapModal;
